import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { CircularProgress, Portal } from '@mui/material'
import { CaretDown, Check, DownloadSimple, Info, Users } from '@phosphor-icons/react'
import dayjs from '../../../utils/dayjsConfig'
import DateRangeFilter from '../../../components/DateRangeFilter'
import LoadingSpinner from '../../../components/LoadingSpinner'
import UbiquaTowerDialog from '../../../components/dialogs/UbiquaTowerDialog'
import UbiquaSearchField from '../../../components/UbiquaSearchField'
import UbiquaTowerToastAlert from '../../../components/alerts/UbiquaTowerToastAlert'
import { downloadReportActivities } from '../helpers/downloads/reportIndexDownloads'
import {
  MAX_ACTIVITY_DOWNLOADS,
  useActiveDownloadCount
} from '../../../components/downloads/fileDownloadStore'
import { activityDownloadLimitHelp } from '../../../constants/fileDownloadConstants'
import useReportExportUsers from '../hooks/useReportExportUsers'
import { REPORT_EXPORT_ACTIVITIES, reportExportConfig } from '../helpers/reportExportConfig'

function SelectionCheckbox({ checked, disabled, onChange }) {
  return (
    <>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      <span className="report-export-check" aria-hidden="true">
        <Check size={12} weight="bold" />
      </span>
    </>
  )
}

export function initialExportRange(startDate, endDate) {
  const end = dayjs(endDate)
  return [end.diff(dayjs(startDate), 'day') >= 31 ? end.subtract(30, 'day') : dayjs(startDate), end]
}

export default function ReportExportDialog({ open, onClose, activeTab, startDate, endDate }) {
  const titleId = useId()
  const usersPanelId = useId()
  const searchRef = useRef(null)
  const [range, setRange] = useState(() => initialExportRange(startDate, endDate))
  const [adjusted, setAdjusted] = useState(false)
  const [activities, setActivities] = useState([])
  const [usersOpen, setUsersOpen] = useState(false)
  const allActivitiesRef = useRef(true)
  const selectedStart = range[0]?.format('YYYY-MM-DD')
  const selectedEnd = range[1]?.format('YYYY-MM-DD')
  const days = range[1]?.diff(range[0], 'day') + 1
  const validDates = Boolean(selectedStart && selectedEnd && days > 0 && days <= 31)
  const config = reportExportConfig(activeTab)
  const catalog = useMemo(
    () => REPORT_EXPORT_ACTIVITIES.filter(([key]) => config.workflows.includes(key)),
    [config]
  )

  useEffect(() => {
    if (!open) return
    allActivitiesRef.current = true
    setRange(initialExportRange(startDate, endDate))
    setAdjusted(dayjs(endDate).diff(dayjs(startDate), 'day') >= 31)
    setUsersOpen(false)
  }, [open, activeTab, startDate, endDate])

  useEffect(() => {
    if (usersOpen) searchRef.current?.focus()
  }, [usersOpen])

  const remote = useReportExportUsers({
    open,
    validDates,
    activeTab,
    startDate: selectedStart,
    endDate: selectedEnd
  })
  const {
    users: visibleUsers,
    totalUsers,
    selectedCount,
    selectedUser,
    allUsers,
    query,
    setQuery,
    loading,
    loadingMore,
    error
  } = remote
  const [errorDismissed, setErrorDismissed] = useState(false)
  useEffect(() => setErrorDismissed(false), [error, open])
  const availableActivities = useMemo(
    () => catalog.map(([key]) => key).filter((key) => remote.activities.includes(key)),
    [catalog, remote.activities, activeTab]
  )

  useEffect(() => {
    setActivities((current) =>
      allActivitiesRef.current
        ? availableActivities
        : current.filter((key) => availableActivities.includes(key))
    )
  }, [availableActivities])

  let usersLabel
  switch (true) {
    case Boolean(allUsers):
      usersLabel = 'Todos los usuarios'
      break
    case Boolean(selectedUser):
      usersLabel = selectedUser.user_name || selectedUser.user_uid
      break
    case Boolean(selectedCount):
      usersLabel = `${selectedCount} usuarios seleccionados`
      break
    default:
      usersLabel = 'Seleccionar usuarios'
  }

  let selectedActivities = availableActivities
  if (!allActivitiesRef.current) {
    selectedActivities = activities.filter((key) => availableActivities.includes(key))
  }

  const hasAvailableActivities = availableActivities.length > 0
  const allActivities =
    hasAvailableActivities && selectedActivities.length === availableActivities.length
  const updateActivities = (values) => {
    allActivitiesRef.current =
      hasAvailableActivities && values.length === availableActivities.length
    setActivities(values)
  }
  const toggle = (set, values, value) =>
    set(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])
  const initializing = !remote.ready && validDates
  const controlsDisabled = !validDates || (loading && totalUsers === 0)
  const activeActivityDownloads = useActiveDownloadCount('activities')
  const activityExportBlocked = activeActivityDownloads >= MAX_ACTIVITY_DOWNLOADS
  const valid =
    validDates &&
    selectedCount > 0 &&
    selectedActivities.length > 0 &&
    !loading &&
    !error &&
    !activityExportBlocked
  const rangeLabel =
    range[0] && range[1]
      ? range[0].isSame(range[1], 'day')
        ? range[0].format('DD/MM/YYYY')
        : `${range[0].format('DD/MM/YYYY')} al ${range[1].format('DD/MM/YYYY')}`
      : 'Selecciona las fechas'
  const submit = () => {
    if (!valid) return
    downloadReportActivities({
      activeTab,
      startDate: selectedStart,
      endDate: selectedEnd,
      userUids: remote.selection.userUids,
      excludedUserUids: remote.selection.excludedUserUids,
      activities: selectedActivities,
      allUsers: remote.selection.allUsers
    })
    onClose()
  }
  return (
    <UbiquaTowerDialog
      open={open}
      onClose={onClose}
      titleId={titleId}
      title={`Reporte de ${config.name} - Todos los usuarios`}
      subtitle="Una fila por actividad, en CSV"
      icon={<DownloadSimple size={18} />}
      className="report-export-modal"
      paperClassName="report-dialog report-export-dialog"
      footer={
        !initializing && (
          <footer className="report-export-footer">
            <span>
              <strong>{rangeLabel}</strong>
              <small>
                {allUsers
                  ? 'Todos los usuarios'
                  : `${selectedCount} ${selectedCount === 1 ? 'usuario' : 'usuarios'}`}{' '}
                ·{' '}
                {`${selectedActivities.length} ${selectedActivities.length === 1 ? 'actividad' : 'actividades'}`}
              </small>
            </span>
            <button type="button" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="report-primary-action"
              disabled={!valid}
              onClick={submit}
            >
              <DownloadSimple size={17} />
              Descargar
            </button>
          </footer>
        )
      }
    >
      <Portal>
        <UbiquaTowerToastAlert
          open={open && Boolean(error) && !errorDismissed}
          severity="error"
          title="No se pudieron cargar las opciones"
          message={error}
          action={{ label: 'Intentar de nuevo', onClick: remote.retry }}
          durationMs={null}
          showCountdown={false}
          onClose={() => setErrorDismissed(true)}
        />
      </Portal>
      {initializing ? (
        <div className="report-export-initial-loading">
          {error ? (
            <div className="report-export-initial-error">
              <p>Vuelve a cargar las opciones para continuar.</p>
              {errorDismissed && (
                <button type="button" onClick={remote.retry}>
                  Intentar de nuevo
                </button>
              )}
            </div>
          ) : (
            <LoadingSpinner size={24} />
          )}
        </div>
      ) : (
        <>
          <div className="report-export-field">
            <div className="report-export-label">
              <span>Rango de fechas</span>
              <small>
                {days || 0} {days === 1 ? 'día' : 'días'}
              </small>
            </div>
            <DateRangeFilter
              value={range}
              onChange={(next) => {
                setRange(next || [])
                setAdjusted(false)
              }}
              onOpen={() => setUsersOpen(false)}
              variant="modern"
              dateDisplayFormat="DD/MM/YYYY"
              maxRangeDays={31}
            />
            <p className={`report-export-note${!validDates ? ' is-error' : ''}`}>
              <Info size={14} />
              <span className="report-export-note-copy">
                {!validDates
                  ? 'Selecciona un rango válido de máximo 31 días.'
                  : adjusted
                    ? 'El periodo de búsqueda pasa de 31 días. Tomamos los últimos 31; puedes cambiar el rango.'
                    : 'Máximo 31 días, contando ambos extremos. Elige un día y luego otro anterior o posterior.'}
              </span>
            </p>
          </div>
          <div
            role="group"
            aria-labelledby={`${usersPanelId}-label`}
            className="report-export-field"
          >
            <div className="report-export-label" id={`${usersPanelId}-label`}>
              Usuarios
            </div>
            <button
              type="button"
              className={`report-export-users-trigger${usersOpen ? ' is-open' : ''}`}
              disabled={controlsDisabled}
              aria-expanded={usersOpen}
              aria-controls={usersPanelId}
              onClick={() => setUsersOpen(!usersOpen)}
            >
              <Users size={18} />
              <span>
                <strong>{usersLabel}</strong>
                {selectedUser && !allUsers && <small>{selectedUser.user_uid}</small>}
              </span>
              {loading ? (
                <CircularProgress
                  className="report-export-trigger-loader"
                  size={15}
                  aria-label="Cargando..."
                />
              ) : (
                <CaretDown size={15} />
              )}
            </button>
            {usersOpen && (
              <div
                className="report-export-users-panel"
                id={usersPanelId}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.stopPropagation()
                    setUsersOpen(false)
                    event.currentTarget.previousElementSibling?.focus()
                  }
                }}
              >
                <UbiquaSearchField
                  className="report-export-search"
                  inputRef={searchRef}
                  disabled={controlsDisabled}
                  ariaLabel="Buscar usuarios para exportar"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                <div className="report-selection-summary">
                  <span>
                    {selectedCount} de {totalUsers} seleccionados
                  </span>
                  <button type="button" disabled={controlsDisabled} onClick={remote.toggleAll}>
                    {allUsers ? 'Quitar todos' : 'Seleccionar todos'}
                  </button>
                </div>
                <div
                  className="report-export-users"
                  onScroll={remote.onScroll}
                  aria-busy={loading || loadingMore}
                >
                  {visibleUsers.map((user) => (
                    <label key={user.user_uid}>
                      <SelectionCheckbox
                        disabled={controlsDisabled}
                        checked={remote.isSelected(user.user_uid)}
                        onChange={() => remote.toggleUser(user.user_uid)}
                      />
                      <span>{user.user_name || user.user_uid}</span>
                      <small>{user.user_uid}</small>
                    </label>
                  ))}
                  {!loading && !error && visibleUsers.length === 0 && (
                    <p>No encontramos usuarios con esa búsqueda.</p>
                  )}
                </div>
                {(loading || loadingMore) && (
                  <div className="report-export-users-loading" role="status">
                    <LoadingSpinner size={16} role={null} text="Cargando..." />
                  </div>
                )}
              </div>
            )}
            {!loading && selectedCount === 0 && (
              <p className="report-export-note is-error">
                <Info size={14} />
                <span className="report-export-note-copy">
                  Elige al menos un usuario para descargar.
                </span>
              </p>
            )}
          </div>
          <div
            role="group"
            aria-labelledby={`${titleId}-activities`}
            className="report-export-field"
          >
            <div className="report-export-label" id={`${titleId}-activities`}>
              <span>Actividades</span>
              <span className="report-export-activity-actions">
                <small>
                  {selectedActivities.length} de {availableActivities.length}
                </small>
                <button
                  type="button"
                  disabled={loading || controlsDisabled || availableActivities.length === 0}
                  onClick={() => updateActivities(allActivities ? [] : availableActivities)}
                >
                  {allActivities ? 'Quitar todas' : 'Seleccionar todas'}
                </button>
              </span>
            </div>
            <div className="report-export-activities">
              {catalog.map(([key, label]) => {
                const enabled = availableActivities.includes(key)
                return (
                  <label
                    className={`report-export-activity${enabled ? '' : ' is-inactive'}`}
                    key={key}
                  >
                    <SelectionCheckbox
                      disabled={loading || controlsDisabled || !enabled}
                      checked={enabled && selectedActivities.includes(key)}
                      onChange={() => toggle(updateActivities, selectedActivities, key)}
                    />
                    <span className="report-export-activity-label">{label}</span>
                    {!enabled && <small>Módulo no activo</small>}
                  </label>
                )
              })}
            </div>
            {selectedActivities.length === 0 && !loading && (
              <p className="report-export-note is-error">
                <Info size={14} />
                <span className="report-export-note-copy">
                  Elige al menos una actividad para descargar.
                </span>
              </p>
            )}
            {activityExportBlocked && (
              <p className="report-export-note is-error">
                <Info size={14} />
                <span className="report-export-note-copy">
                  {activityDownloadLimitHelp(MAX_ACTIVITY_DOWNLOADS)}
                </span>
              </p>
            )}
          </div>
        </>
      )}
    </UbiquaTowerDialog>
  )
}
